import { useHistory } from 'react-router-dom'
import { client } from '../../../hooks/Client'
import { useFirestoreGeneral, useFirestoreQuery } from '../../../firebase/useFirestore'
import impactChannels from '../impactInBeeldConfig.js'
import { deriveChannelStatus, buildTodoList } from '../../../helpers/impactInBeeld/channelStatus'
import { deriveActionNeeded } from '../../../helpers/impactInBeeld/deriveActionNeeded'
import formatEuro from '../../../helpers/formatEuro'
import StatusPill from '../StatusPill.jsx'
import StaticCardLabel from '../../dashboard/StaticCardLabel'
import notAttributedText from '../../../helpers/msiMonetisationHelpers/notAttributedText'

/**
 * Kerncijfer tile for the Overzicht tab.
 *
 * @param {object} props
 * @param {string} props.title - Label above the number
 * @param {string|null} props.value - Formatted value, or null to show the placeholder
 * @param {string} [props.emptyNote] - Honest empty-state note shown instead of a fake number
 * @param {string|null} [props.subLabel] - Small context line under the value (e.g. methodiek-versielabel)
 * @param {React.ReactNode} [props.note] - Extra regel onder het getal, bv. hoeveel metingen niet zijn doorgerekend
 */
const StatTile = ({ title, value, emptyNote, subLabel = null, note = null }) => {

    return (
        <div className='participation-value-card'>
            <StaticCardLabel title={title} />
            {value !== null ? (
                <>
                    <p className='participation-value-amount'>{value}</p>
                    {subLabel && <p className='impact-per-euro-kpi-label'>{subLabel}</p>}
                    {note}
                </>
            ) : emptyNote ? (
                <p className='impact-in-beeld-stat-empty'>{emptyNote}</p>
            ) : (
                <p className='participation-value-amount participation-value-amount--placeholder'>—</p>
            )}
        </div>
    )
}

/**
 * Overzicht tab: kerncijfers, kanaalstatuslijst en "nog te doen".
 * Sinds fase 2: QR-scans uit de publicaties, en 'Actie nodig'-detectie op
 * gepubliceerde kanalen (prijswijziging/product weg/kengetal-drift) met een
 * herpubliceer-link naar het configscherm. Mensen geholpen blijft een
 * eerlijke lege staat tot het live aggregaat (fase 3).
 * See docs/features/impact-in-beeld-fase2.md.
 *
 * @param {object} props
 * @param {ReturnType<typeof import('../../../hooks/useImpactPerEuroRatio').default>} props.impactPerEuro - Kengetal-resultaat, éénmalig opgehaald door de hub
 */
const OverviewTab = ({ impactPerEuro }) => {
    const history = useHistory()

    const channelDocs = useFirestoreGeneral('ImpactChannels', 'CompagnyID', client)
    const publications = useFirestoreGeneral('ImpactPublications', 'CompagnyID', client)
    const products = useFirestoreGeneral('Products', 'CompagnyID', client)
    // Impactfeed server-side begrensd: zonder limit zou de hele (onbegrensd
    // groeiende) transactielog naar de browser streamen voor 10 regels.
    // orderBy op Timestamp is hier veilig — docs zijn server-only geschreven,
    // het veld bestaat dus altijd. Vereist composite index CompagnyID+Timestamp
    // (firestore.indexes.json).
    const recentTransactions = useFirestoreQuery('ImpactTransactions', {
        orderBy: ['Timestamp', 'desc'],
        limit: 10,
        initial: [],
    })
    const { ratio, totalValue, result, label, error, errorMessage, notAttributedCount, notAttributedReasons } = impactPerEuro
    // Methodiek § 6.3: hoeveel metingen niet zijn doorgerekend, met de weg naar
    // de uitleg per reden op de detailpagina (#344).
    const notAttributedNote = notAttributedCount > 0 ? (
        <p className='impact-per-euro-kpi-note'>
            {notAttributedText(notAttributedCount, notAttributedReasons)}
            <button type='button' className='impact-per-euro-link' onClick={() => history.push(`/${client}/impact-per-euro`)}>
                Wat je kunt doen →
            </button>
        </p>
    ) : null

    // Vóór de eerste snapshot geen actie-nodig-oordeel — anders flitst
    // 'Actie nodig' terwijl de publicaties nog laden.
    const publicationsLoaded = Array.isArray(publications)

    const orgPublication = (publications || []).find((publication) => publication.Target === 'org')

    const scanTotal = (publications || [])
        .reduce((sum, publication) => sum + (Number(publication.ScanCount) || 0), 0)

    // Gepubliceerde kanalen kunnen verouderd raken — dan telt de afgeleide
    // status ('action-needed') overal: pill, statuslijst én nog-te-doen.
    const effectiveDoc = (doc) => {
        if (!publicationsLoaded || !doc || deriveChannelStatus(doc) !== 'live') return doc

        const { actionNeeded } = deriveActionNeeded({
            publishedAtMs: doc.PublishedAt,
            publishedKengetal: orgPublication?.Snapshot?.Kengetal ?? null,
            currentKengetal: ratio,
            shownProducts: products || [],
            shownProductIds: doc.Config?.ProductIds || [],
        })

        return actionNeeded ? { ...doc, Status: 'action-needed' } : doc
    }

    const effectiveDocs = (channelDocs || []).map(effectiveDoc)

    const docForChannel = (channelId) =>
        effectiveDocs.find((candidate) => candidate.ChannelId === channelId)

    const todoItems = buildTodoList(impactChannels, effectiveDocs)

    const todoLabel = (item) =>
        item.reason === 'concept'
            ? `${item.title}: nog niet gepubliceerd`
            : `${item.title}: verouderd — publiceer opnieuw`

    return (
        <div className='impact-in-beeld-tab'>
            <h2>Overzicht</h2>
            {error && (
                <p className='impact-in-beeld-form-error'>{errorMessage}</p>
            )}
            <div className='impact-in-beeld-stat-row'>
                <StatTile title='Impact per euro' value={ratio !== null ? formatEuro(ratio) : null} subLabel={label} note={notAttributedNote} />
                <StatTile title='Maatschappelijke waarde' value={totalValue !== null ? formatEuro(totalValue) : null} />
                <StatTile title='Mensen geholpen' value={null} emptyNote='Beschikbaar zodra transacties binnenkomen.' />
                <StatTile
                    title='QR-scans'
                    value={(publications?.length || 0) > 0 ? String(scanTotal) : null}
                    emptyNote='Beschikbaar zodra je QR-codes live zijn.'
                />
            </div>
            {!error && ratio === null && (
                <p className='impact-in-beeld-tab-notice'>
                    {result?.status === 'onvoldoende' && result.reden
                        ? result.reden
                        : 'Vul je jaarlijkse kosten in bij Instellingen → Algemeen om impact per euro te tonen.'}
                </p>
            )}
            <div className='impact-in-beeld-overview-section'>
                <h3>Kanaalstatus</h3>
                <ul className='impact-in-beeld-status-list' aria-label='Kanaalstatus'>
                    {impactChannels.map((channel) => (
                        <li key={channel.id}>
                            <span>{channel.title}</span>
                            <StatusPill status={deriveChannelStatus(docForChannel(channel.id))} />
                        </li>
                    ))}
                </ul>
            </div>
            <div className='impact-in-beeld-overview-section'>
                <h3>Recente transacties</h3>
                {recentTransactions.length === 0 ? (
                    <p className='impact-in-beeld-tab-intro'>
                        Nog geen transacties ontvangen — verbind je kassa of webshop via de
                        Integraties-tab.
                    </p>
                ) : (
                    <ul className='impact-in-beeld-status-list' aria-label='Recente transacties'>
                        {recentTransactions.map((transaction) => (
                            <li key={transaction.ID}>
                                <span>
                                    {formatEuro(transaction.Amount)}
                                    {transaction.Value !== null && ` → ${formatEuro(transaction.Value)} impact`}
                                </span>
                                <span className='impact-in-beeld-feed-time'>
                                    {transaction.Timestamp?.toMillis
                                        ? new Date(transaction.Timestamp.toMillis()).toLocaleString('nl-NL')
                                        : ''}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <div className='impact-in-beeld-overview-section'>
                <h3>Nog te doen</h3>
                {todoItems.length === 0 ? (
                    <p className='impact-in-beeld-tab-intro'>Geen actiepunten — alles staat goed.</p>
                ) : (
                    <ul className='impact-in-beeld-todo-list' aria-label='Nog te doen'>
                        {todoItems.map((item) => (
                            <li key={item.channelId}>
                                <button
                                    type='button'
                                    className='impact-in-beeld-todo-action'
                                    onClick={() => history.push(`/${client}/impactinbeeld/kanalen/${item.channelId}`)}
                                >
                                    {todoLabel(item)}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    )
}

export default OverviewTab
